import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import gibletLogo from '@/assets/giblet-logo.png';

export default function DemoCallForKnowledgeDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState('Construction Safety Standards for AI-Driven Modular Fabrication');
  const [scope, setScope] = useState(
    'Document comprehensive safety standards and protocols for integrating AI systems into modular fabrication workflows. This KB should cover: (1) AI-specific safety protocols for autonomous fabrication equipment, (2) Human-AI collaboration safety frameworks on fabrication floors, (3) Quality control checkpoints for AI-driven module assembly, (4) Emergency shutdown procedures for AI-controlled systems, (5) Compliance mapping to OSHA 1926 and relevant ANSI standards.'
  );
  const [format, setFormat] = useState('structured-document');
  const [minRequirements, setMinRequirements] = useState(
    'Minimum 15 pages of structured content with cited references. Must include at least 3 case studies from active construction projects. All safety protocols must reference current OSHA/ANSI standard numbers.'
  );
  const [references, setReferences] = useState(
    'CURT Owner Safety Blueprint (2023)\nCURT Owners Blueprint for Modularization\niSPAI POC Deliverables — Safety Module\nOSHA 1926 Subpart R — Steel Erection\nANSI/ASSE A10.47 — Work Zone Safety'
  );
  const [acceptanceCriteria, setAcceptanceCriteria] = useState(
    '1. All safety protocols validated against current OSHA standards\n2. Peer review by at least 2 domain experts\n3. AI integration scenarios tested against real fabrication workflows\n4. Complete traceability from standard to implementation guidance'
  );

  const handleNext = () => {
    navigate('/demo-call-for-knowledge/review', {
      state: {
        ...location.state,
        kbTitle: title,
        kbScope: scope,
        kbFormat: format,
        kbMinRequirements: minRequirements,
        kbReferences: references,
        kbAcceptanceCriteria: acceptanceCriteria,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Knowledge Gap Details</h1>
          <p className="text-lg text-muted-foreground">
            Define the scope and requirements for the knowledge base
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-5">
          <div className="space-y-2">
            <Label>Knowledge Base Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Knowledge Scope & Description</Label>
            <Textarea value={scope} onChange={(e) => setScope(e.target.value)} rows={5} />
          </div>

          <div className="space-y-2">
            <Label>Expected KB Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="structured-document">Structured Document (Sections & Subsections)</SelectItem>
                <SelectItem value="technical-standard">Technical Standard / Specification</SelectItem>
                <SelectItem value="case-study-collection">Case Study Collection</SelectItem>
                <SelectItem value="best-practice-guide">Best Practice Guide</SelectItem>
                <SelectItem value="training-material">Training Material / Curriculum</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Minimum Content Requirements</Label>
            <Textarea value={minRequirements} onChange={(e) => setMinRequirements(e.target.value)} rows={3} />
          </div>

          <div className="space-y-2">
            <Label>Reference Materials & Links</Label>
            <Textarea value={references} onChange={(e) => setReferences(e.target.value)} rows={4} />
          </div>

          <div className="space-y-2">
            <Label>Acceptance Criteria</Label>
            <Textarea value={acceptanceCriteria} onChange={(e) => setAcceptanceCriteria(e.target.value)} rows={4} />
          </div>

          <div className="space-y-2">
            <Label>Supporting Documents (optional)</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground text-sm">
              Drag & drop files here or click to browse (max 5 files)
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-knowledge/bounty')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={!title || !scope} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
